import { useMemo, useCallback, useState } from 'react'
import {
  Card,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts'
import { useRoutingMetrics } from '@/hooks/useAnalytics'
import { useVersionContext } from '@/context/VersionContext'

const COLORS = ['#4A90D9', '#A0AEC0', '#68D391']

const MISSING_INFO_PATTERNS = [
  'Disposition is missing or could not be determined',
  'Charge type is missing or could not be determined',
  'Charge is missing or could not be determined',
  'Charge description is missing or could not be determined',
  'Age at time of offense could not be calculated',
]

const renderActiveShape = (props: any) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
    midAngle, percent,
  } = props
  const RADIAN = Math.PI / 180
  const labelRadius = (innerRadius + outerRadius + 10) / 2
  const lx = cx + labelRadius * Math.cos(-midAngle * RADIAN)
  const ly = cy + labelRadius * Math.sin(-midAngle * RADIAN)

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <text
        x={lx}
        y={ly}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#fff"
        fontSize={13}
        fontWeight={700}
        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    </g>
  )
}

function isMissingInfoReason(reason: string): boolean {
  return MISSING_INFO_PATTERNS.some(p => reason.startsWith(p))
}

function computeSummary(stats: any, heatmapData: any[]) {
  const agg = (idFilter: (id: string) => boolean, repFilter: (r: string) => boolean) => {
    const matching = heatmapData.filter(d => idFilter(d.idLevel) && repFilter(d.reportability))
    return {
      total: matching.reduce((s: number, d: any) => s + d.total, 0),
      autoCount: matching.reduce((s: number, d: any) => s + d.autoCount, 0),
      exclusionCount: matching.reduce((s: number, d: any) => s + d.exclusionCount, 0),
      exclusionAutoCount: matching.reduce((s: number, d: any) => s + d.exclusionAutoCount, 0),
    }
  }

  const neiAgg = agg(() => true, r => r === 'Not Enough Info')
  const baseAuto = agg(id => id === 'High', r => r === 'Not Reportable')
  const idReview = agg(id => id !== 'High', r => r === 'Not Reportable')
  const autoExclCount = baseAuto.exclusionCount + neiAgg.exclusionAutoCount + idReview.exclusionCount

  const hiAgg = agg(() => true, r => r !== 'Not Reportable' && r !== 'Reportable')
  const hiExclCount = hiAgg.exclusionCount
  const auditorAgg = agg(() => true, r => r === 'Reportable')

  const aiAutomated = (baseAuto.total + neiAgg.autoCount + idReview.total) - autoExclCount
  const researcherCount = auditorAgg.total + (hiAgg.total - hiExclCount)

  const qaReportable = auditorAgg.total
  const qaNonReportable = aiAutomated
  const researcher = researcherCount - qaReportable

  const bd = stats?.insufficientDataBreakdown
  let missingInfo = 0
  let aiUnable = 0

  if (bd) {
    missingInfo += bd.adjudicationBanned || 0
    const validationReasons: Array<{ reason: string; count: number }> = bd.validationReasons || []
    let missingInfoFromValidation = 0
    for (const vr of validationReasons) {
      if (isMissingInfoReason(vr.reason)) missingInfoFromValidation += vr.count
    }
    const totalValidationReasonCount = validationReasons.reduce((s, vr) => s + vr.count, 0)
    const validationFailedOffenses = bd.validationFailed || 0
    if (totalValidationReasonCount > 0 && validationFailedOffenses > 0) {
      const missingRatio = missingInfoFromValidation / totalValidationReasonCount
      missingInfo += Math.round(validationFailedOffenses * missingRatio)
      aiUnable += Math.round(validationFailedOffenses * (1 - missingRatio))
    } else {
      aiUnable += validationFailedOffenses
    }
    missingInfo += bd.dispositionInProgress || 0
    aiUnable += bd.manualReview || 0
    aiUnable += bd.ambiguousClassification || 0
    aiUnable += bd.idOnly || 0
    aiUnable += bd.other || 0
  }

  const totalBd = missingInfo + aiUnable
  if (totalBd > 0 && totalBd !== researcher) {
    const scale = researcher / totalBd
    missingInfo = Math.round(missingInfo * scale)
    aiUnable = researcher - missingInfo
  }

  return {
    reviewed: qaReportable + qaNonReportable + researcher,
    qaReportable,
    qaNonReportable,
    researcher,
    missingInfo,
    aiUnable,
  }
}

function parseHeatmap(stats: any) {
  if (!stats?.heatmap) return []
  return stats.heatmap.map((entry: any) => ({
    idLevel: entry.identity_level || 'Unknown',
    reportability: entry.reportability || 'Unknown',
    total: entry.total,
    autoCount: entry.autoCount,
    exclusionCount: entry.exclusionCount || 0,
    exclusionAutoCount: entry.exclusionAutoCount || 0,
  }))
}

function buildSummaryFromProductAutomation(stats: any) {
  const paSummary: any[] = (stats.productAutomationSummary || [])
    .filter((r: any) => r.sourceNorm === 'Courts')
  if (paSummary.length === 0) return null

  let aiAutomation = 0
  let auditor = 0
  let researcher = 0
  for (const row of paSummary) {
    aiAutomation += (row.aiExclusion ?? 0) + (row.aiAutomated ?? 0)
    auditor += row.auditor ?? 0
    researcher += row.researcher ?? 0
  }

  const qaReportable = auditor
  const qaNonReportable = aiAutomation
  const reviewed = qaReportable + qaNonReportable + researcher

  const bd = stats?.insufficientDataBreakdown
  let missingInfo = 0
  let aiUnable = 0
  if (bd) {
    missingInfo += bd.adjudicationBanned || 0
    const validationReasons: Array<{ reason: string; count: number }> = bd.validationReasons || []
    let missingInfoFromValidation = 0
    for (const vr of validationReasons) {
      if (isMissingInfoReason(vr.reason)) missingInfoFromValidation += vr.count
    }
    const totalValidationReasonCount = validationReasons.reduce((s, vr) => s + vr.count, 0)
    const validationFailedOffenses = bd.validationFailed || 0
    if (totalValidationReasonCount > 0 && validationFailedOffenses > 0) {
      const missingRatio = missingInfoFromValidation / totalValidationReasonCount
      missingInfo += Math.round(validationFailedOffenses * missingRatio)
      aiUnable += Math.round(validationFailedOffenses * (1 - missingRatio))
    } else {
      aiUnable += validationFailedOffenses
    }
    missingInfo += bd.dispositionInProgress || 0
    aiUnable += bd.manualReview || 0
    aiUnable += bd.ambiguousClassification || 0
    aiUnable += bd.idOnly || 0
    aiUnable += bd.other || 0
  }
  const totalBd = missingInfo + aiUnable
  if (totalBd > 0 && totalBd !== researcher) {
    const scale = researcher / totalBd
    missingInfo = Math.round(missingInfo * scale)
    aiUnable = researcher - missingInfo
  }
  return { reviewed, qaReportable, qaNonReportable, researcher, missingInfo, aiUnable }
}

interface WorkloadBreakdownCardProps {
  startDate: string
  endDate: string
}

export function WorkloadBreakdownCard({ startDate, endDate }: WorkloadBreakdownCardProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const onPieEnter = useCallback((_: any, index: number) => setActiveIndex(index), [])
  const onPieLeave = useCallback(() => setActiveIndex(null), [])
  const { selectedVersion } = useVersionContext()

  const { data: metricsData, isLoading } = useRoutingMetrics({ startDate, endDate, modelVersion: selectedVersion || undefined })
  const stats = metricsData?.metrics || null
  const tat = (metricsData?.metrics as any)?.turnaroundTime || null
  const heatmapData = useMemo(() => parseHeatmap(stats), [stats])

  const summaryData = useMemo(() => {
    if (!stats) return null
    return buildSummaryFromProductAutomation(stats) ?? computeSummary(stats, heatmapData)
  }, [stats, heatmapData])

  const chartData = useMemo(() => {
    if (!summaryData || summaryData.reviewed === 0) return []
    return [
      { name: 'QA reportable', value: summaryData.qaReportable },
      { name: 'QA non-reportable', value: summaryData.qaNonReportable },
      { name: 'Researcher', value: summaryData.researcher },
    ]
  }, [summaryData])

  const roundedPcts = useMemo(() => {
    if (!summaryData || summaryData.reviewed === 0) return {} as Record<string, number>
    const items = [
      { key: 'qaReportable', value: summaryData.qaReportable },
      { key: 'qaNonReportable', value: summaryData.qaNonReportable },
      { key: 'researcher', value: summaryData.researcher },
    ]
    const total = summaryData.reviewed
    const rawPcts = items.map(i => ({ key: i.key, raw: (i.value / total) * 100 }))
    const floored = rawPcts.map(p => ({ ...p, floor: Math.floor(p.raw), remainder: p.raw - Math.floor(p.raw) }))
    let diff = 100 - floored.reduce((s, p) => s + p.floor, 0)
    floored.sort((a, b) => b.remainder - a.remainder)
    for (const p of floored) {
      if (diff > 0) { p.floor += 1; diff-- }
    }
    const result: Record<string, number> = {}
    for (const p of floored) result[p.key] = p.floor
    return result
  }, [summaryData])

  const pct = (key: string) => `${roundedPcts[key] ?? 0}%`

  if (isLoading && !metricsData) {
    return (
      <Card withBorder padding="xl">
        <Group justify="center" py="xl">
          <Loader size="md" />
        </Group>
      </Card>
    )
  }

  if (!summaryData || summaryData.reviewed === 0) return null

  return (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Group align="flex-start" gap={60} wrap="wrap">
        <Stack gap="md" style={{ minWidth: 240, flex: 1 }}>
          <div>
            <Title order={4} fw={700}>Offense Level Dashboard</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Reviewed ({summaryData.reviewed.toLocaleString()})
            </Text>
          </div>

          {[
            { label: 'QA reportable', key: 'qaReportable', color: COLORS[0] },
            { label: 'QA non-reportable', key: 'qaNonReportable', color: COLORS[1] },
            { label: 'Researcher', key: 'researcher', color: COLORS[2] },
          ].map((item) => (
            <Group key={item.label} gap="xs">
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
              <Text size="sm" c="dimmed">
                {item.label} ({pct(item.key)})
              </Text>
            </Group>
          ))}

          {summaryData.researcher > 0 && (() => {
            const miPct = Math.round((summaryData.missingInfo / summaryData.researcher) * 100)
            const aiPct = 100 - miPct
            return (
              <Stack gap={4} ml={18} pl={12} style={{ borderLeft: `2px solid ${COLORS[2]}` }}>
                <Text size="xs" c="dimmed" lh={1.4}>
                  <Text span fw={500} c="dark">Missing Info</Text> — {miPct}%
                  <br />
                  <Text span fs="italic" size="xs">e.g. DOB, Release date from prison, Disposition</Text>
                </Text>
                <Text size="xs" c="dimmed" lh={1.4}>
                  <Text span fw={500} c="dark">AI Unable to Decide</Text> — {aiPct}%
                  <br />
                  <Text span fs="italic" size="xs">e.g. "Boating while Intoxicated" is DUI vs Traffic</Text>
                </Text>
              </Stack>
            )
          })()}
        </Stack>

        <div style={{ flex: 1, minWidth: 280, maxWidth: 360 }}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                activeIndex={activeIndex ?? undefined}
                activeShape={renderActiveShape}
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                dataKey="value"
                onMouseEnter={onPieEnter}
                onMouseLeave={onPieLeave}
                stroke="none"
              >
                {chartData.map((_, index) => (
                  <Cell
                    key={index}
                    fill={COLORS[index]}
                    opacity={activeIndex !== null && activeIndex !== index ? 0.4 : 1}
                    style={{ transition: 'opacity 0.2s ease' }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Group>

          {tat && (
            <>
              <Divider my="lg" />
              <Title order={5} fw={700} mb="sm">TAT</Title>
              <Text size="sm" c="dimmed">
                Average per offense:{' '}
                <Text span fw={600} c="dark">{(tat.avgTotalMs / 1000).toFixed(1)} seconds</Text>
              </Text>
            </>
          )}
    </Card>
  )
}
